import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import type { BreadcrumbItem } from './../models/navigation/Breadcrumb.model';
import type { Locale } from '#i18n';

interface BreadcrumbConfig {
  translationKey: string;
  icon?: string;
  subPages?: Record<string, BreadcrumbConfig>;
}

/* TODO: Breadcrumb mapping generalized for all projects, different handling for different projects */
const BREADCRUMB_MAPPING: Record<string, BreadcrumbConfig> = {
  user: {
    translationKey: 'nav.profile',
    subPages: {
      '': { translationKey: 'user.personal_data' },
      payment: { translationKey: 'user.payment_methods' },
      'auth-keys': { translationKey: 'user.auth_keys' },
      usergroup: { translationKey: 'user.usergroup' },
      'account-and-security': { translationKey: 'user.account_security' },
    },
  },
  invoices: {
    translationKey: 'nav.invoices',
  },
  agb: {
    translationKey: 'nav.agb',
  },
  'privacy-policy': {
    translationKey: 'nav.privacy_policy',
  },
  imprint: {
    translationKey: 'nav.imprint',
  },
};

export function useBreadcrumb() {
  const route = useRoute();
  const { availableLocales, t } = useI18n();

  const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
    try {
      if (!route.path) {
        return getDefaultBreadcrumb();
      }

      const pathSegments = route.path.split('/').filter((segment) => segment);
      let currentPath = '';
      const items: BreadcrumbItem[] = [
        { label: t('nav.main.dashboard'), to: '/', icon: 'fa6-solid:house' },
      ];

      for (let i = 0; i < pathSegments.length; i++) {
        const segment = pathSegments[i];

        if (!segment?.trim()) {
          continue;
        }

        if (i === 0 && availableLocales.includes(segment as Locale)) {
          currentPath += `/${segment}`;
          continue;
        }

        currentPath += `/${segment}`;
        const isLastSegment = i === pathSegments.length - 1;

        if (isNumericId(segment)) {
          items.push({
            label: segment,
            to: isLastSegment ? undefined : currentPath,
          });
          continue;
        }

        const breadcrumbConfig = BREADCRUMB_MAPPING[segment];
        if (breadcrumbConfig) {
          const nextSegment = pathSegments[i + 1];
          const subPageConfig = nextSegment
            ? breadcrumbConfig.subPages?.[nextSegment]
            : breadcrumbConfig.subPages?.[''];

          if (subPageConfig && nextSegment) {
            items.push({
              label: t(breadcrumbConfig.translationKey),
              to: currentPath,
              icon: breadcrumbConfig.icon,
            });

            currentPath += `/${nextSegment}`;
            items.push({
              label: t(subPageConfig.translationKey),
              to: i + 1 === pathSegments.length - 1 ? undefined : currentPath,
              icon: subPageConfig.icon,
            });
            i++;
          } else {
            items.push({
              label: t(breadcrumbConfig.translationKey),
              to: isLastSegment ? undefined : currentPath,
              icon: breadcrumbConfig.icon,
            });
          }
        } else {
          items.push({
            label: segment,
            to: isLastSegment ? undefined : currentPath,
          });
        }
      }

      return items;
    } catch (error) {
      console.error('Error generating breadcrumbs:', error);
      return getDefaultBreadcrumb();
    }
  });

  return { breadcrumbItems };
}

function getDefaultBreadcrumb(): BreadcrumbItem[] {
  const { t } = useI18n();
  return [{ label: t('nav.main.dashboard'), to: '/', icon: 'fa6-solid:house' }];
}

function isNumericId(segment: string): boolean {
  return !isNaN(Number(segment)) && segment.length > 0;
}
